import type { WizardData, WizardField, WizardFieldError } from '@hive/api';
import { useI18n, type CommonKey } from '@hive/i18n';
import type { ReactNode } from 'react';
import { CheckCards, SecretField, SelectField, Switch, TagsField, TextAreaField, TextField, type Option } from '../fields';
import { detectedValue, fieldOptions, lines } from './schema';

/** Fonte das opções que dependem da empresa (perfis, permissões, domínios da API). */
export type OptionsByType = Partial<Record<string, Option[]>>;

/** Campo desenhado pela tela que usa o assistente (ex.: a matriz de permissões por tela do editor de perfil). */
export type CustomFields = Partial<Record<string, (p: { value: unknown; onChange: (v: unknown) => void; error?: ReactNode; label: string; help?: string }) => ReactNode>>;

export interface FieldRendererProps {
  field: WizardField;
  data: WizardData;
  error?: WizardFieldError;
  onChange: (id: string, value: unknown) => void;
  dynamicOptions?: OptionsByType;
  /** Em edição, segredo em branco mantém o valor guardado no cofre. */
  secretKeep?: boolean;
  groupLabel?: (g: string) => string;
  customFields?: CustomFields;
}

const MULTI_DYNAMIC = new Set(['profile-multi', 'permission-multi', 'api-scope-multi']);

/** Desenha um campo a partir do esquema do assistente (A12: tela gerada do esquema, sem código específico). */
export function FieldRenderer({ field: f, data, error, onChange, dynamicOptions, secretKeep, groupLabel, customFields }: FieldRendererProps) {
  const { t, l } = useI18n<CommonKey>();
  const label = l(f.label) || f.id;
  const help = l(f.help) || undefined;
  const err: ReactNode = error ? (
    <>
      {error.what}
      {error.fix ? ` ${error.fix}` : ''}
    </>
  ) : undefined;
  const v = data[f.id];
  // R12-01: valor detectado pelo servidor trocado ou apagado → "Detectado: … · usar o detectado"
  const det = detectedValue(f, v);
  const detected = det ? (
    <span className="wz-detected">
      {t('wz.detected', { value: det })} ·{' '}
      <button type="button" className="link-btn" onClick={() => onChange(f.id, det)}>
        {t('wz.useDetected')}
      </button>
    </span>
  ) : undefined;
  const example = f.example && !['email-list', 'cidr-list'].includes(f.type) ? t('wz.example', { value: f.example }) : undefined;
  const textHint: ReactNode = detected ? (
    <>
      {help && <>{help} </>}
      {detected}
    </>
  ) : (
    help ?? example
  );
  const str = v == null ? '' : Array.isArray(v) ? v.join('\n') : String(v);
  const common = { label, required: f.required, error: err, help };
  const custom = customFields?.[f.type];
  if (custom) return <>{custom({ value: v, onChange: (x) => onChange(f.id, x), error: err, label, help })}</>;

  switch (f.type) {
    case 'textarea':
      return <TextAreaField {...common} value={str} maxLength={f.maxLength} hint={help ?? example} onChange={(x) => onChange(f.id, x)} />;
    case 'email-list':
    case 'cidr-list':
      return (
        <TextAreaField
          {...common}
          rows={5}
          value={str}
          placeholder={f.example}
          hint={help ?? (f.type === 'email-list' ? t('wz.emailListHint') : t('wz.cidrListHint'))}
          onChange={(x) => onChange(f.id, x)}
        />
      );
    case 'secret':
      return <SecretField {...common} value={str} keepHint={secretKeep} onChange={(x) => onChange(f.id, x)} />;
    case 'boolean':
      return <Switch checked={v === true || v === 'true'} label={label} hint={help} onChange={(x) => onChange(f.id, x)} />;
    case 'select':
      return (
        <SelectField
          {...common}
          hint={help}
          value={str}
          options={[...(f.required && str ? [] : [{ value: '', label: '—' }]), ...fieldOptions(f, l)]}
          onChange={(x) => onChange(f.id, x)}
        />
      );
    case 'multiselect':
      return (
        <CheckCards
          legend={label}
          hint={help}
          error={err}
          value={Array.isArray(v) ? v.map(String) : lines(v)}
          options={fieldOptions(f, l)}
          onChange={(x) => onChange(f.id, x)}
        />
      );
    case 'tags':
      return <TagsField {...common} value={Array.isArray(v) ? v.map(String) : lines(v)} hint={help} onChange={(x) => onChange(f.id, x)} />;
    case 'number':
      return <TextField {...common} type="number" value={str} hint={help ?? example} onChange={(x) => onChange(f.id, x === '' ? '' : Number(x))} />;
    default:
      if (MULTI_DYNAMIC.has(f.type)) {
        return (
          <CheckCards
            legend={label}
            hint={help}
            error={err}
            value={Array.isArray(v) ? v.map(String) : lines(v)}
            options={dynamicOptions?.[f.type] ?? []}
            groupLabel={groupLabel}
            onChange={(x) => onChange(f.id, x)}
          />
        );
      }
      return (
        <TextField
          {...common}
          type={f.type === 'email' ? 'email' : f.type === 'url' ? 'url' : 'text'}
          value={str}
          maxLength={f.maxLength}
          suffix={f.suffix}
          placeholder={f.type === 'url' ? 'https://' : undefined}
          hint={textHint}
          autoComplete={f.type === 'email' ? 'email' : 'off'}
          onChange={(x) => onChange(f.id, x)}
        />
      );
  }
}
